import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchLatestNews,
  fetchCategoryNews,
  searchNews,
  normalizeArticle,
  getQuota,
} from '../utils/api';
import {
  saveBackup,
  loadBackup,
  getBackupAge,
} from '../utils/backup';

/**
 * Custom hook for fetching news with infinite scroll and backup fallback.
 *
 * Routes to the correct API endpoint based on the mode:
 *   - 'latest'   → /v1/latest-news (home page, no category filter)
 *   - 'category'  → /v1/search?category=X (category pages)
 *   - 'search'    → /v1/search?keywords=X (search results)
 *
 * On 429 (rate limit), loads from localStorage backup and exposes
 * `usingBackup` + `backupAge` for the UI to display a banner.
 *
 * @param {Object} options
 * @param {string} [options.mode='latest'] - Fetch mode: 'latest', 'category', or 'search'.
 * @param {string} [options.category='general'] - Category ID (used when mode='category').
 * @param {string} [options.searchQuery=''] - Search query (used when mode='search').
 * @param {number} [options.pageSize=10] - Results per page (max 20 on free tier).
 * @returns {Object} News state and controls.
 */
const useNews = ({
  mode = 'latest',
  category = 'general',
  searchQuery = '',
  pageSize = 10,
} = {}) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [quota, setQuota] = useState(() => getQuota());
  const [usingBackup, setUsingBackup] = useState(false);
  const [backupAge, setBackupAge] = useState(null);

  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadingRef = useRef(false);
  const pageTokenRef = useRef(null);

  /** Build a cache key for backup storage based on mode/params. */
  const getCacheKey = useCallback(() => {
    if (mode === 'search') return `search:${searchQuery}:${pageSize}`;
    if (mode === 'category') return `cat:${category}:${pageSize}`;
    return `latest:en:${pageSize}`;
  }, [mode, category, searchQuery, pageSize]);

  /** Core fetch function that routes to the correct API endpoint. */
  const fetchNews = useCallback(
    async (isLoadMore = false) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        let result;
        const token = isLoadMore ? pageTokenRef.current : null;

        if (mode === 'search' && searchQuery.trim()) {
          result = await searchNews(searchQuery, pageSize, token);
        } else if (mode === 'category') {
          result = await fetchCategoryNews(category, pageSize, token);
        } else {
          result = await fetchLatestNews('en', pageSize, token);
        }

        const normalized = result.articles.map(normalizeArticle);

        setArticles((prev) => (isLoadMore ? [...prev, ...normalized] : normalized));
        pageTokenRef.current = result.nextPageToken;
        setHasMore(result.hasMore);
        setQuota(getQuota());

        // Save to backup on successful live fetch
        if (!isLoadMore) {
          const cacheKey = getCacheKey();
          saveBackup(cacheKey, normalized);
          setUsingBackup(false);
          setBackupAge(null);
        }
      } catch (err) {
        const isRateLimit = err.message === 'rate_limit';

        if (isRateLimit && !isLoadMore) {
          // Try loading from backup on rate limit
          const cacheKey = getCacheKey();
          const backup = loadBackup(cacheKey);

          if (backup) {
            const normalized = backup.articles.map(normalizeArticle);
            setArticles(normalized);
            setHasMore(false); // no pagination for backup data
            setQuota(getQuota());
            setUsingBackup(true);
            setBackupAge(getBackupAge(cacheKey));
            setError(null);
          } else {
            setError(err.message || 'Failed to fetch news');
            setUsingBackup(false);
            setBackupAge(null);
          }
        } else {
          setError(err.message || 'Failed to fetch news');
        }
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [mode, category, searchQuery, pageSize, getCacheKey]
  );

  /** Reset and fetch when mode/category/searchQuery changes. */
  useEffect(() => {
    setArticles([]);
    setUsingBackup(false);
    setBackupAge(null);
    pageTokenRef.current = null;
    setHasMore(true);
    fetchNews(false);
  }, [fetchNews]);

  /** Set up IntersectionObserver for infinite scroll. */
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          fetchNews(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observerRef.current.observe(currentRef);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, fetchNews]);

  /** Manual retry on error. */
  const retry = useCallback(() => {
    setError(null);
    pageTokenRef.current = null;
    fetchNews(false);
  }, [fetchNews]);

  return {
    articles,
    loading,
    error,
    hasMore,
    loadMoreRef,
    retry,
    quota,
    usingBackup,
    backupAge,
  };
};

export default useNews;
